import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Stack,
    TextField,
    InputAdornment,
    MenuItem,
    LinearProgress,
    Skeleton,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    FormControlLabel,
    Switch,
    Alert,
    Snackbar,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    MoreHoriz as MoreIcon,
    Warning as AlertIcon,
    Download as ExportIcon,
    Update as UpdateIcon,
    Assignment as UsageIcon,
    Settings as SettingsIcon,
    Business as SupplierIcon,
    Tune as ManageIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Construction as PartIcon,
    History as HistoryIcon,
    AttachMoney as MoneyIcon
} from '@mui/icons-material';
import inventoryService from '../../services/inventoryService';

const SparePartsPage = () => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [modelFilter, setModelFilter] = useState('All');

    const [categories, setCategories] = useState([]);
    const [models, setModels] = useState([]);

    // Selection & Modals
    const [selectedItem, setSelectedItem] = useState(null);
    const [managerDialog, setManagerDialog] = useState(false);
    const [managerTab, setManagerTab] = useState(0);

    // Form States
    const [partForm, setPartForm] = useState({
        item_code: '',
        manufacturer_part_number: '',
        item_name: '',
        item_description: '',
        category_main: '',
        category_sub: '',
        compatible_models: [],
        unit: 'PCS',
        minimum_stock: 5,
        reorder_level: 10,
        reorder_quantity: 20,
        unit_cost: 0,
        unit_price: 0,
        is_critical: false,
        image_url: ''
    });

    const [supplierForm, setSupplierForm] = useState({
        supplier_name: '',
        contact_name: '',
        email: '',
        phone: '',
        address: '',
        category_specialization: ''
    });

    const [stockForm, setStockForm] = useState({
        type: 'IN',
        quantity: '',
        reference: '',
        notes: ''
    });

    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

    const fetchFilters = async () => {
        try {
            const [catRes, modelRes] = await Promise.all([
                inventoryService.getCategories(),
                inventoryService.getModels()
            ]);
            if (catRes.success) setCategories(catRes.data);
            if (modelRes.success) setModels(modelRes.data);
        } catch (error) {
            console.error("Failed to fetch filters:", error);
        }
    };

    const fetchInventory = async () => {
        setLoading(true);
        try {
            const response = await inventoryService.getAllItems({
                search: searchTerm,
                category: categoryFilter,
                status: statusFilter,
                model: modelFilter !== 'All' ? modelFilter : undefined
            });
            if (response.success) setItems(response.data);
        } catch (error) {
            console.error("Failed to fetch inventory:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFilters();
    }, []);

    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchInventory();
        }, 500);
        return () => clearTimeout(debounce);
    }, [searchTerm, categoryFilter, statusFilter, modelFilter]);

    const filteredItems = useMemo(() => items, [items]);

    const handleOpenManager = (editItem = null) => {
        if (editItem) {
            setPartForm({
                ...editItem,
                compatible_models: typeof editItem.compatible_models === 'string'
                    ? JSON.parse(editItem.compatible_models)
                    : editItem.compatible_models || []
            });
            setSelectedItem(editItem);
            setManagerTab(0);
        } else {
            setPartForm({
                item_code: '',
                manufacturer_part_number: '',
                item_name: '',
                item_description: '',
                category_main: '',
                category_sub: '',
                compatible_models: [],
                unit: 'PCS',
                minimum_stock: 5,
                reorder_level: 10,
                reorder_quantity: 20,
                unit_cost: 0,
                unit_price: 0,
                is_critical: false,
                image_url: ''
            });
            setSelectedItem(null);
            setManagerTab(0);
        }
        setManagerDialog(true);
    };

    const handlePartSubmit = async () => {
        try {
            let res;
            if (partForm.id) {
                res = await inventoryService.updateItem(partForm.id, partForm);
            } else {
                res = await inventoryService.addItem(partForm);
            }

            if (res.success) {
                setNotification({ open: true, message: `Part ${partForm.id ? 'updated' : 'added'} successfully`, type: 'success' });
                setManagerDialog(false);
                fetchInventory();
                fetchFilters();
            }
        } catch (error) {
            setNotification({ open: true, message: error.response?.data?.message || 'Operation failed', type: 'error' });
        }
    };

    const handleStockSubmit = async () => {
        if (!selectedItem) {
            setNotification({ open: true, message: 'Please select a part from the table first', type: 'warning' });
            return;
        }
        try {
            const payload = {
                quantity: parseInt(stockForm.quantity),
                reference_number: stockForm.reference,
                notes: stockForm.notes
            };

            let res;
            if (stockForm.type === 'IN') {
                res = await inventoryService.addStock(selectedItem.item_code, payload);
            } else {
                res = await inventoryService.removeStock(selectedItem.item_code, payload);
            }

            if (res.success) {
                setNotification({ open: true, message: 'Stock updated successfully', type: 'success' });
                setStockForm({ type: 'IN', quantity: '', reference: '', notes: '' });
                fetchInventory();
            }
        } catch (error) {
            setNotification({ open: true, message: error.response?.data?.message || 'Transaction failed', type: 'error' });
        }
    };

    const handleSupplierSubmit = async () => {
        try {
            const res = await inventoryService.addSupplier(supplierForm);
            if (res.success) {
                setNotification({ open: true, message: 'Supplier added successfully', type: 'success' });
                setSupplierForm({ supplier_name: '', contact_name: '', email: '', phone: '', address: '', category_specialization: '' });
            }
        } catch (error) {
            setNotification({ open: true, message: error.response?.data?.message || 'Failed to add supplier', type: 'error' });
        }
    };

    return (
        <Box p={4} sx={{ bgcolor: 'grey.50', minHeight: '100vh', transition: '0.3s' }}>
            {/* Actions — no page title, the top bar already names the page */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<ExportIcon />} sx={{ borderRadius: '12px', fontWeight: 700, px: 3 }}>Export CSV</Button>
                    <Tooltip title="Manage Parts, Suppliers & Stock">
                        <Button
                            variant="contained"
                            className="gradient-primary"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenManager()}
                            sx={{ borderRadius: '12px', fontWeight: 800, px: 3, boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)' }}
                        >
                            Inventory Manager
                        </Button>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Filter Bar */}
            <Card sx={{ mb: 4, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'grey.100' }}>
                <CardContent sx={{ py: 2.5 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} lg={4}>
                            <TextField fullWidth size="small" placeholder="Search by MPN or Component..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="disabled" /></InputAdornment>, sx: { borderRadius: '12px', bgcolor: 'grey.50' } }} />
                        </Grid>
                        <Grid item xs={6} lg={2}>
                            <TextField select fullWidth size="small" label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                                <MenuItem value="All">Global Catalog</MenuItem>
                                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} lg={2}>
                            <TextField select fullWidth size="small" label="Model" value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                                <MenuItem value="All">All Models</MenuItem>
                                {models.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} lg={2}>
                            <TextField select fullWidth size="small" label="Availability" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                                <MenuItem value="All">All Items</MenuItem>
                                <MenuItem value="ACTIVE">In Circulation</MenuItem>
                                <MenuItem value="INACTIVE">Inactive/Obsolete</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} lg={2} display="flex" justifyContent="flex-end">
                            <IconButton sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }} onClick={() => fetchInventory()}><UpdateIcon fontSize="small" /></IconButton>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Data Table */}
            <TableContainer component={Card} sx={{ borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid', borderColor: 'grey.100' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: 'text.secondary', py: 2 }}>VISUAL</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: 'text.secondary' }}>IDENTIFICATION</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: 'text.secondary' }}>CATEGORY</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: 'text.secondary', width: 250 }}>QUOTA STATUS</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: 'text.secondary' }}>VALUATION</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, fontSize: '11px', color: 'text.secondary' }}>ACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? [...Array(6)].map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={6}><Skeleton height={80} sx={{ borderRadius: '12px' }} /></TableCell></TableRow>
                        )) : filteredItems.map((item) => {
                            const isLowStock = item.current_stock <= item.reorder_level;
                            const stockPct = Math.min((item.current_stock / (item.reorder_level * 3)) * 100, 100);
                            const isSelected = selectedItem?.id === item.id;
                            return (
                                <TableRow
                                    key={item.id}
                                    hover
                                    selected={isSelected}
                                    onClick={() => setSelectedItem(item)}
                                    sx={{ cursor: 'pointer', transition: '0.2s', '&.Mui-selected': { bgcolor: 'primary.50' } }}
                                >
                                    <TableCell>
                                        <Avatar
                                            variant="rounded"
                                            src={item.image_url}
                                            sx={{ width: 56, height: 56, bgcolor: 'primary.50', color: 'primary.main', borderRadius: '16px', border: '1px solid', borderColor: 'primary.100' }}
                                        >
                                            <PartIcon />
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>{item.item_name}</Typography>
                                            {isLowStock && <Tooltip title="Low Stock Warning"><AlertIcon color="error" sx={{ fontSize: 16 }} /></Tooltip>}
                                            {item.is_critical && <Chip label="CRITICAL" color="error" size="small" sx={{ height: 16, fontSize: '8px', fontWeight: 900 }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, opacity: 0.6 }}>MPN: {item.manufacturer_part_number}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.category_main} size="small" sx={{ fontWeight: 800, borderRadius: '8px', fontSize: '10px', bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.200' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="caption" fontWeight={900} color={isLowStock ? 'error' : 'primary'}>{item.current_stock} {item.unit || 'Units'}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>Target: {item.reorder_level}</Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={stockPct}
                                            color={isLowStock ? 'error' : 'primary'}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.100' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={900} color="success.main">
                                            ${(parseFloat(item.unit_price) || 0).toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 700 }}>Cost: ${(parseFloat(item.unit_cost) || 0).toFixed(2)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <Tooltip title="Quick Edit">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenManager(item); }}>
                                                    <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton size="small"><MoreIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <Box p={3} display="flex" justifyContent="space-between" alignItems="center" bgcolor="grey.50" borderTop="1px solid" borderColor="grey.100">
                    <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ letterSpacing: 1 }}>AUDITED DATASET • ISO-9001 COMPLIANT</Typography>
                    <Pagination count={1} size="small" color="primary" sx={{ '& .MuiPaginationItem-root': { fontWeight: 800 } }} />
                </Box>
            </TableContainer>

            {/* Comprehensive Inventory Manager Dialog */}
            <Dialog
                open={managerDialog}
                onClose={() => setManagerDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '28px', minHeight: '600px' } }}
            >
                <DialogTitle sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><ManageIcon /></Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight={900} letterSpacing="-0.5px">
                                {partForm.id ? `Adjusting ${partForm.item_code}` : 'Central Inventory Manager'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>SYSTEM OVERRIDE CONSOLE</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setManagerDialog(false)} sx={{ bgcolor: 'grey.100' }}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Tabs
                        value={managerTab}
                        onChange={(e, v) => setManagerTab(v)}
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: 'grey.50' }}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label="Profile" icon={<PartIcon />} iconPosition="start" sx={{ fontWeight: 900, textTransform: 'none', py: 2 }} />
                        <Tab label="Stock Policy" icon={<UsageIcon />} iconPosition="start" sx={{ fontWeight: 900, textTransform: 'none', py: 2 }} disabled={!partForm.item_code} />
                        <Tab label="Quick Adjust" icon={<HistoryIcon />} iconPosition="start" sx={{ fontWeight: 900, textTransform: 'none', py: 2 }} disabled={!partForm.id} />
                        <Tab label="Suppliers" icon={<SupplierIcon />} iconPosition="start" sx={{ fontWeight: 900, textTransform: 'none', py: 2 }} />
                    </Tabs>

                    <Box p={4}>
                        {managerTab === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="System Item Code (Internal SKU)" value={partForm.item_code} onChange={(e) => setPartForm({ ...partForm, item_code: e.target.value })} disabled={!!partForm.id} variant="filled" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Manufacturer Reference (MPN)" value={partForm.manufacturer_part_number} onChange={(e) => setPartForm({ ...partForm, manufacturer_part_number: e.target.value })} variant="filled" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Technical Display Name" value={partForm.item_name} onChange={(e) => setPartForm({ ...partForm, item_name: e.target.value })} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { fontWeight: 800 } }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline rows={2} label="Extended Part Description" value={partForm.item_description} onChange={(e) => setPartForm({ ...partForm, item_description: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Primary Category" value={partForm.category_main} onChange={(e) => setPartForm({ ...partForm, category_main: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Secondary Category" value={partForm.category_sub} onChange={(e) => setPartForm({ ...partForm, category_sub: e.target.value })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Cloud Image Link" value={partForm.image_url} onChange={(e) => setPartForm({ ...partForm, image_url: e.target.value })} />
                                </Grid>
                            </Grid>
                        )}

                        {managerTab === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField fullWidth type="number" label="Shelf Min Qty" value={partForm.minimum_stock} onChange={(e) => setPartForm({ ...partForm, minimum_stock: parseInt(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField fullWidth type="number" label="Critical Threshold" value={partForm.reorder_level} onChange={(e) => setPartForm({ ...partForm, reorder_level: parseInt(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField fullWidth type="number" label="Default Order Qty" value={partForm.reorder_quantity} onChange={(e) => setPartForm({ ...partForm, reorder_quantity: parseInt(e.target.value) })} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField fullWidth label="Unit (PCS/LTR)" value={partForm.unit} onChange={(e) => setPartForm({ ...partForm, unit: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={4.5}>
                                    <TextField fullWidth type="number" label="Landed Unit Cost ($)" value={partForm.unit_cost} onChange={(e) => setPartForm({ ...partForm, unit_cost: parseFloat(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4.5}>
                                    <TextField fullWidth type="number" label="List Price ($)" value={partForm.unit_price} onChange={(e) => setPartForm({ ...partForm, unit_price: parseFloat(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Card variant="outlined" sx={{ p: 2, borderRadius: '16px', bgcolor: partForm.is_critical ? 'error.50' : 'grey.50', borderColor: partForm.is_critical ? 'error.100' : 'grey.200' }}>
                                        <FormControlLabel
                                            control={<Switch checked={partForm.is_critical} onChange={(e) => setPartForm({ ...partForm, is_critical: e.target.checked })} color="error" />}
                                            label={<Typography fontWeight={900}>FLAG AS MISSION-CRITICAL COMPONENT</Typography>}
                                        />
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {managerTab === 2 && (
                            <Box>
                                <Box sx={{ mb: 4, p: 3, borderRadius: '20px', bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
                                    <Typography variant="h6" fontWeight={800} color="primary.dark">Live Stock Adjustment</Typography>
                                    <Typography variant="caption" fontWeight={700}>MODIFIES SYSTEM INVENTORY COUNT IN REAL-TIME</Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <TextField select fullWidth label="Movement Type" value={stockForm.type} onChange={(e) => setStockForm({ ...stockForm, type: e.target.value })}>
                                            <MenuItem value="IN">ADD (+) Stock Reception</MenuItem>
                                            <MenuItem value="OUT">SUB (-) Part Dispatch</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <TextField fullWidth type="number" label="Quantity" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <TextField fullWidth label="Reference #" value={stockForm.reference} onChange={(e) => setStockForm({ ...stockForm, reference: e.target.value })} placeholder="PO-XXXX" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth multiline rows={3} label="Movement Reason / Notes" value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            startIcon={<SaveIcon />}
                                            onClick={handleStockSubmit}
                                            disabled={!stockForm.quantity}
                                            sx={{ py: 2, borderRadius: '16px', fontWeight: 900 }}
                                        >
                                            Commit Stock Movement
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {managerTab === 3 && (
                            <Box>
                                <Typography variant="h6" fontWeight={800} mb={3}>Add New Global Supplier</Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}><TextField fullWidth label="Official Entity Name" value={supplierForm.supplier_name} onChange={(e) => setSupplierForm({ ...supplierForm, supplier_name: e.target.value })} /></Grid>
                                    <Grid item xs={12} md={6}><TextField fullWidth label="Key Account Manager" value={supplierForm.contact_name} onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })} /></Grid>
                                    <Grid item xs={12} md={6}><TextField fullWidth label="Procurement Email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} /></Grid>
                                    <Grid item xs={12} md={6}><TextField fullWidth label="Contact Number" value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} /></Grid>
                                    <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Corporate Headquarters Address" value={supplierForm.address} onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} /></Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            sx={{ p: 2, borderRadius: '14px', fontWeight: 900, bgcolor: 'grey.900', color: 'white', '&:hover': { bgcolor: 'black' } }}
                                            startIcon={<SupplierIcon />}
                                            onClick={handleSupplierSubmit}
                                            disabled={!supplierForm.supplier_name}
                                        >
                                            Register Supplier to Network
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 4, py: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.100' }}>
                    <Button onClick={() => setManagerDialog(false)} sx={{ fontWeight: 800, color: 'text.secondary' }}>Cancel Override</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    {managerTab < 2 && (
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handlePartSubmit}
                            disabled={!partForm.item_code || !partForm.item_name}
                            sx={{ borderRadius: '14px', px: 5, fontWeight: 900, py: 1.5 }}
                            className="gradient-primary"
                        >
                            {partForm.id ? 'Authorize Updates' : 'Sync New Part to Ledger'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={5000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={notification.type} variant="filled" sx={{ borderRadius: '16px', fontWeight: 800, boxShadow: '0 8px 25px rgba(0,0,0,0.15)' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SparePartsPage;
